<script setup lang="ts">
  import type { TooltipContentEmits, TooltipContentProps } from 'reka-ui'
  import type { HTMLAttributes } from 'vue'
  import { reactiveOmit } from '@vueuse/core'
  import {
    injectTooltipRootContext,
    TooltipArrow,
    TooltipContent,
    TooltipPortal,
    useForwardPropsEmits
  } from 'reka-ui'
  import { computed } from 'vue'
  import { cn } from '../../../lib/utils'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<TooltipContentProps & { class?: HTMLAttributes['class'] }>(),
    {
      sideOffset: 4
    }
  )

  const emits = defineEmits<TooltipContentEmits>()

  const delegatedProps = reactiveOmit(props, 'class')
  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const tooltipContext = injectTooltipRootContext()
  const open = computed(() => tooltipContext.open.value)
  const { zIndex } = useDialogZIndex(open)
</script>

<template>
  <TooltipPortal>
    <TooltipContent
      data-slot="tooltip-content"
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'bg-foreground text-background z-50 w-fit text-balance rounded-md px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          props.class
        )
      "
      :style="{ zIndex }"
    >
      <slot />

      <TooltipArrow :width="11" :height="6" class="fill-foreground" />
    </TooltipContent>
  </TooltipPortal>
</template>
